import { Component, OnInit, Input, Injector } from "@angular/core";
import { SupplierProductAssortmentService, AuthenticationService, ProductValidity } from 'src/app/_services';
import { LxmDialog } from 'src/app/_helpers/dialogs';
import { ActivatedRoute } from '@angular/router';
import { ISupplierProductAssortment, ISupplierProductAssortmentFormData } from './supplier-product-status.model';
import { Subscription } from 'rxjs';
import { SupplierProductAssortmentStatus, Bool, UserAction, ProductCardSection } from 'src/app/enum';
import { IProductCardFormData, IRetailChain, IProductCard, IProductCardRetailChain } from '../product-info/product-info.model';
import { AppState } from 'src/app/state/app.state';
import { appSettings } from 'src/app/app.settings';
import { LxmMessage } from 'src/app/_helpers';
import { ProductCardBase } from "../product-card-base";
import { LOCAL_STORAGE_KEY } from "src/app/config";
import { SupplierProductAssortmentDeliveryStatusDialog, SupplierProductStatusBaseSchemaDialog } from "./dialog/supplier-product-status-schema.dialog";
import { ProductHeaderState } from "../product-header/product-header.state";

@Component({
  selector: "supplier-product-status-card",
  templateUrl: "./supplier-product-status.card.html",
  styleUrls: ["./supplier-product-status.card.scss"]
})
export class SupplierProductStatusCard
  extends ProductCardBase<ISupplierProductAssortment[]>
  implements OnInit {
  public title = "cards.products.product_status.title";

  public UserAction = UserAction;
  public Bool = Bool;
  public ProductAssortmentStatus = SupplierProductAssortmentStatus;

  public cardSection = ProductCardSection.AssortmentSchemes;

  public columns: string[] = [];

  public productCard: IProductCard;
  public assortments: ISupplierProductAssortment[];
  public expandedElements = [];

  public formData: IProductCardFormData;
  public retailChains: IProductCardRetailChain[];
  public retailChainsById: { [key: string]: IProductCardRetailChain };

  @Input()
  public productId: string;

  public cardValid = true;
  public canEdit: boolean;
  public isOwnProduct: boolean;

  private _loadBaseAssortmentsSubscription: Subscription;

  public isExpandedKey =
    LOCAL_STORAGE_KEY.PRODUCT_SUPPLIER_PRODUCT_STATUS_CARD_OPEN;

  constructor(
    injector: Injector,
    private _appState: AppState,
    private _dialog: LxmDialog,
    private _supplierProductAssortmentService: SupplierProductAssortmentService,
    route: ActivatedRoute,
    private _authService: AuthenticationService,
    public productValidity: ProductValidity,
    private _msg: LxmMessage,
    private _productHeaderState: ProductHeaderState) {
    super(
      injector,
      "productSupplierAssortment",
      "productSupplierAssortmentChanged"
    );

    this.productCard = route.snapshot.data.productCard as IProductCard;

    this.isOwnProduct = this.productCard.isOwnProduct;
    this.assortments = this.productCard.assortments;
    this.formData = this.productCard.formData;
    this.retailChains = this.formData.retailChains.filter((x) => !x.isClient);
    this.retailChainsById = this.retailChains.reduce((dict, x) => {
      dict[x.id] = x;
      return dict;
    }, {});
  }

  ngOnInit() {
    super.ngOnInit();
    this.canEdit = this._authService.hasRight([UserAction.ManageProducts]);
    this._setTableColumns();
  }


  private _setTableColumns() {
    if (this.isOwnProduct) {
      this.columns = [
        "assortment_base_valid",
        "assortment_base_status",
        "assortment_base_delivery_status",
        "assortment_base_visibility",
        "assortment_base_user",
        "list_actions"
      ];
    } else {
      this.columns = [
        "assortment_base_valid",
        "assortment_base_status",
        "assortment_base_user"
      ];
    }
  }

  public isAvailable(
    row: ISupplierProductAssortment,
    chainId: string
  ): boolean {
    if (!row.retailerIds || row.retailerIds.length === 0) {
      return row.retailersListIsExclusive;
    }

    const ix = row.retailerIds?.indexOf(chainId);
    return row.retailersListIsExclusive ? ix < 0 : ix >= 0;
  }

  public openBaseSchemaDialog(row?: ISupplierProductAssortment) {
    if ((row && !row.isEditable) || !this.canEdit) {
      return;
    }

    this._dialog.open(SupplierProductStatusBaseSchemaDialog, {
      ...appSettings.DIALOG_SIZES.M,
      data: {
        assortmentCard: this,
        retailChains: this.retailChains,
        schema: row
      }
      // resolve: () => {
      //   const resolvers: any = {
      //     formData: this._supplierProductAssortmentService.getFormData(this.productId)
      //   };
      //   return forkJoin(resolvers);
      // }
    });
  }

  public loadBaseAssortments(): void {
    if (this._loadBaseAssortmentsSubscription) {
      this._loadBaseAssortmentsSubscription.unsubscribe();
    }

    this._loadBaseAssortmentsSubscription =
      this._supplierProductAssortmentService
        .getGridData(this.productId)
        .subscribe(
          (result) => {
            this.assortments = result;
          },
          (err) => {
            console.error(err);
            this._msg.error({
              message:
                "cards.products.product_status.table.load_failed"
            });
          }
        );
  }

  //

  public toggleExpandRow(row) {
    event.stopImmediatePropagation();
    if (this.expandedElements.includes(row)) {
      let index = this.expandedElements.indexOf(row);
      this.expandedElements.splice(index, 1);
    } else {
      this.expandedElements.push(row);
    }
  }

  public openDeliveryStatusDialog(row?: ISupplierProductAssortment) {
    if ((row && !row.isCurrent) || !this.canEdit) {
      return;
    }

    this._dialog.open(SupplierProductAssortmentDeliveryStatusDialog, {
      ...appSettings.DIALOG_SIZES.S,
      data: {
        assortmentCard: this,
        retailChains: this.retailChains,
        schema: row
      }
    }, () => {
      const currentSupplierAssortment = this.assortments?.length ? this.assortments?.find((x) => x.isCurrent) : null;
      this._productHeaderState.setData({ supplierAssortmentDeliveryStatus: currentSupplierAssortment?.deliveryStatus });
    });
  }
}

export interface IProductAssortmentDialogData {
  formData: ISupplierProductAssortmentFormData;
  assortmentCard: SupplierProductStatusCard;
  retailChains: IRetailChain[];
  schema: ISupplierProductAssortment;
}
